// Bodega en el celular (pedido de Rodrigo, 2026-10-25): app para los bodegueros. Dos trabajos, rápidos
// y en terreno: recibir el retorno de las ODD (cuenta lo que baja del camión; la diferencia queda como
// faltante a cargo del chofer) y ajustar stock en línea, sin trabas, siempre trazable. Más el historial
// del día para revisar lo hecho. Vive en #/bodega, fuera del shell de escritorio; todo lo que hace aquí
// también se puede hacer desde el ERP (mismas rutas de la API, mismos permisos).
// Armar ODD (Rodrigo, 2026-10-30): quien tiene `logistica.od.crear` (Fabián, rol `operador_odd`) arma la
// orden de despacho en el celular: elige las notas pendientes, el camión y el personal de ruta. Preparar,
// validar la carga, facturar e imprimir siguen en el PC.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { api, nuevaClaveIdempotencia } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import {
  useBuscarProductos,
  useDebounce,
  useDisponibilidad,
  usePresentaciones,
  useProducto,
  useUbicaciones,
} from '../api/consultas';
import type { Presentacion, ProductoEncontrado, Ubicacion } from '../api/contratos';
import { coincide } from '../comun/coincide';
import { PanelError } from '../comun/Estados';
import { decimal, fecha } from '../comun/formato';
import {
  clavesLog,
  type NotaDisponible,
  useChoferes,
  useDisponibles,
  useRutas,
  useVehiculos,
} from '../logistica/datos';
import { CambiarClave } from '../sesion/CambiarClave';
import { useSesion } from '../sesion/SesionContexto';
import { useStockDeBodega } from '../stock/datos';
import './bodega.css';

interface LineaRetorno {
  productoId: string;
  sku: string;
  descripcion: string;
  cantidad: string;
}
interface OdPorRecibir {
  odId: string;
  numero: number;
  version: number;
  fecha: string;
  chofer: string | null;
  patente: string | null;
  ruta: string | null;
  lineas: LineaRetorno[];
}
interface ResultadoRecepcion {
  faltantes: number;
  sobrantes: number;
  ajusteFaltanteNumero: number | null;
  ajusteSobranteNumero: number | null;
}
interface GuiaResumen {
  id: string;
  numero: number;
  ubicacionId: string;
  sentido: 'ENTRADA' | 'SALIDA';
  concepto: string;
  motivo: string;
  fechaNegocio: string;
  creadoEn: string;
  creadoPor: string | null;
  lineas: number;
  unidades: string;
}
interface GuiaLeida extends GuiaResumen {
  lineasDetalle: Array<{
    numero: number;
    productoId: string;
    cantidadBase: string;
    saldoAntes: string;
    saldoDespues: string;
  }>;
}

const limpiar = (t: string) => t.replace(/[^\d.,]/g, '').replace(',', '.');
const num = (t: string | undefined) => Number(limpiar(t ?? '') || '0');
/** Número → texto para la API y para `decimal` (hasta 4 decimales, sin ceros de más). */
const texto = (n: number) => String(Math.round(n * 10_000) / 10_000);
const hora = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
const hoyLocal = () => new Date().toLocaleDateString('sv-SE');

/** Las cajas del producto (presentaciones activas mayores que la unidad), de mayor a menor. */
function cajasDe(presentaciones: readonly Presentacion[] | undefined) {
  return (presentaciones ?? [])
    .filter((p) => p.activa && !p.es_base && Number(p.factor_a_base) > 1)
    .sort((a, b) => Number(b.factor_a_base) - Number(a.factor_a_base));
}

/** «28 CJ + 4 UN»: cajas completas y unidades sueltas, como se cuenta en bodega. */
function enCajas(base: number, caja: Presentacion | null, unidad: string): string {
  if (!caja) return `${decimal(texto(base))} ${unidad}`;
  const factor = Number(caja.factor_a_base);
  const signo = base < 0 ? '−' : '';
  const abs = Math.abs(base);
  const cajas = Math.floor(abs / factor + 1e-9);
  const sueltas = Number((abs - cajas * factor).toFixed(4));
  const partes = [
    cajas ? `${decimal(texto(cajas))} ${caja.unidad_codigo}` : '',
    sueltas || !cajas ? `${decimal(texto(sueltas))} ${unidad}` : '',
  ].filter(Boolean);
  return signo + partes.join(' + ');
}

/** Refresca solo: lo que hace una persona se ve en las demás pantallas sin recargar. */
const CADA = 15_000;
const CLAVE_BODEGA = 'madeci.bodega.ubicacion';

type Vista = 'camiones' | 'odd' | 'ajustar' | 'historial';

function usePorRecibir(empresaId: string, habilitado: boolean) {
  return useQuery({
    queryKey: ['emp', empresaId, 'bodega', 'por-recibir'],
    queryFn: () => api.get<OdPorRecibir[]>('/logistica/por-recibir', { query: { empresaId } }),
    refetchInterval: CADA,
    enabled: habilitado,
  });
}

/** La bodega en la que trabaja (se recuerda en este teléfono; BMA si no eligió). */
function useBodegaDelTurno(empresaId: string) {
  const ubicaciones = useUbicaciones(empresaId);
  const bodegas = useMemo(
    () => (ubicaciones.data ?? []).filter((u) => u.tipo === 'BODEGA'),
    [ubicaciones.data],
  );
  const [id, setId] = useState(() => {
    try {
      return localStorage.getItem(CLAVE_BODEGA) ?? '';
    } catch {
      return '';
    }
  });
  const valida =
    bodegas.find((b) => b.id === id) ?? bodegas.find((b) => b.codigo === 'BMA') ?? bodegas[0];
  const elegir = (nuevo: string) => {
    setId(nuevo);
    try {
      localStorage.setItem(CLAVE_BODEGA, nuevo);
    } catch {
      /* sin almacenamiento: vale solo por esta vez */
    }
  };
  return { bodegas, bodega: valida ?? null, ubicaciones: ubicaciones.data ?? [], elegir };
}

// ================================================================ marco de la app

export function Bodega() {
  const s = useSesion();
  const empresaId = s.empresa!.id;
  const recibe = s.puede('logistica.od.recibir');
  const ajusta = s.puede('inventario.ajuste.registrar');
  const arma = s.puede('logistica.od.crear');
  // Mientras cargan los permisos se muestra Camiones; quien no recibe camiones parte en Ajustar.
  const [elegida, setVista] = useState<Vista | null>(null);
  const vista: Vista = elegida ?? (recibe || !s.permisosCargados ? 'camiones' : 'ajustar');
  const [menu, setMenu] = useState(false);
  const [clave, setClave] = useState(false);
  const turno = useBodegaDelTurno(empresaId);
  // Instalada como app («MADECI Bodega»), la pestaña y el selector de apps muestran este nombre.
  useEffect(() => {
    const antes = document.title;
    document.title = 'MADECI Bodega';
    return () => {
      document.title = antes;
    };
  }, []);
  const pendientes = usePorRecibir(empresaId, recibe).data?.length ?? 0;
  const iniciales = (s.usuario?.nombre ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div className="bod">
      <header className="bod-barra">
        <div className="bod-marca">
          <span className="bod-marca-logo">MADECI</span>
          <span className="bod-marca-app">Bodega</span>
        </div>
        {turno.bodegas.length > 1 ? (
          <label className="bod-ubic">
            <span className="bod-oculto">Bodega</span>
            <select
              value={turno.bodega?.id ?? ''}
              onChange={(e) => turno.elegir(e.target.value)}
              aria-label="Bodega"
            >
              {turno.bodegas.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.codigo}
                </option>
              ))}
            </select>
          </label>
        ) : (
          turno.bodega && <span className="bod-ubic bod-ubic-fija">{turno.bodega.codigo}</span>
        )}
        <button
          type="button"
          className="bod-avatar"
          aria-label="Menú"
          aria-expanded={menu}
          onClick={() => setMenu((m) => !m)}
        >
          {iniciales}
        </button>
      </header>

      {menu && (
        <div className="bod-velo" onClick={() => setMenu(false)}>
          <div className="bod-menu" role="menu" onClick={(e) => e.stopPropagation()}>
            <div className="bod-menu-quien">
              <strong>{s.usuario?.nombre}</strong>
              <span>{s.usuario?.correo}</span>
              <span>{s.empresa?.nombre_fantasia ?? s.empresa?.razon_social ?? ''}</span>
            </div>
            {s.puede('ventas.nota.ver') && (
              <a role="menuitem" href="#/" onClick={() => setMenu(false)}>
                Ir al ERP completo
              </a>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setMenu(false);
                setClave(true);
              }}
            >
              Cambiar mi clave
            </button>
            <button
              type="button"
              role="menuitem"
              className="bod-menu-salir"
              onClick={() => void s.cerrar()}
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
      {clave && <CambiarClave alCerrar={() => setClave(false)} />}

      <main className="bod-cuerpo">
        {vista === 'camiones' && (recibe ? <Camiones /> : <SinPermiso que="recibir camiones" />)}
        {vista === 'odd' &&
          (!arma ? (
            <SinPermiso que="armar órdenes de despacho" />
          ) : turno.bodega ? (
            <ArmarOdd bodega={turno.bodega} />
          ) : (
            <p className="bod-vacio">Cargando bodegas…</p>
          ))}
        {vista === 'ajustar' &&
          (ajusta ? (
            turno.bodega ? (
              <Ajustar bodega={turno.bodega} ubicaciones={turno.ubicaciones} />
            ) : (
              <p className="bod-vacio">Cargando bodegas…</p>
            )
          ) : (
            <SinPermiso que="ajustar stock" />
          ))}
        {vista === 'historial' && <Historial bodega={turno.bodega} />}
      </main>

      <nav className="bod-nav" aria-label="Secciones">
        <BotonNav
          activo={vista === 'camiones'}
          onClick={() => setVista('camiones')}
          icono={<IcoCamion />}
          insignia={pendientes}
        >
          Camiones
        </BotonNav>
        {/* Solo quien arma ODD ve el botón: al resto de la bodega no le aparece. */}
        {arma && (
          <BotonNav activo={vista === 'odd'} onClick={() => setVista('odd')} icono={<IcoOdd />}>
            Armar ODD
          </BotonNav>
        )}
        <BotonNav
          activo={vista === 'ajustar'}
          onClick={() => setVista('ajustar')}
          icono={<IcoAjuste />}
        >
          Ajustar stock
        </BotonNav>
        <BotonNav
          activo={vista === 'historial'}
          onClick={() => setVista('historial')}
          icono={<IcoReloj />}
        >
          Historial
        </BotonNav>
      </nav>
    </div>
  );
}

function BotonNav({
  activo,
  onClick,
  icono,
  insignia,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  icono: ReactNode;
  insignia?: number;
  children: ReactNode;
}) {
  return (
    <button type="button" aria-current={activo ? 'page' : undefined} onClick={onClick}>
      <span className="bod-nav-ico">
        {icono}
        {insignia ? <span className="bod-insignia">{insignia}</span> : null}
      </span>
      <span>{children}</span>
    </button>
  );
}

function SinPermiso({ que }: { que: string }) {
  return (
    <Recuadro titulo="Sin permiso">
      <p className="bod-vacio">No tienes permiso para {que}. Pídelo al administrador.</p>
    </Recuadro>
  );
}

function Recuadro({
  titulo,
  extra,
  tono,
  children,
}: {
  titulo: ReactNode;
  extra?: ReactNode;
  tono?: 'ok' | 'aviso';
  children: ReactNode;
}) {
  return (
    <section className="bod-recuadro" data-tono={tono}>
      <h2>
        <span>{titulo}</span>
        {extra && <span className="bod-recuadro-extra">{extra}</span>}
      </h2>
      <div className="bod-recuadro-cuerpo">{children}</div>
    </section>
  );
}

/** − [cantidad] + : el teclado numérico o los botones, lo que sea más rápido. */
function Cantidad({
  valor,
  onChange,
  etiqueta,
  grande,
}: {
  valor: string;
  onChange: (v: string) => void;
  etiqueta: string;
  grande?: boolean;
}) {
  const paso = (d: number) => onChange(texto(Math.max(0, num(valor) + d)));
  return (
    <div className="bod-cant" data-grande={grande || undefined}>
      <button type="button" aria-label={`Menos ${etiqueta}`} onClick={() => paso(-1)}>
        −
      </button>
      <input
        inputMode="decimal"
        aria-label={etiqueta}
        value={valor}
        placeholder="—"
        onFocus={(e) => e.target.select()}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.,]/g, ''))}
      />
      <button type="button" aria-label={`Más ${etiqueta}`} onClick={() => paso(1)}>
        +
      </button>
    </div>
  );
}

// ================================================================ camiones (retorno de ODD)

function Camiones() {
  const { empresa } = useSesion();
  const q = usePorRecibir(empresa!.id, true);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [hecho, setHecho] = useState<{ od: OdPorRecibir; r: ResultadoRecepcion } | null>(null);
  const od = q.data?.find((x) => x.odId === abierta) ?? null;

  if (hecho) return <RecepcionHecha od={hecho.od} r={hecho.r} alSeguir={() => setHecho(null)} />;
  if (od)
    return (
      <RecibirOd
        od={od}
        alVolver={() => setAbierta(null)}
        alTerminar={(r) => {
          setHecho({ od, r });
          setAbierta(null);
        }}
      />
    );
  return (
    <Recuadro
      titulo="Camiones por recibir"
      extra={q.data ? `${q.data.length} pendiente${q.data.length === 1 ? '' : 's'}` : undefined}
    >
      {q.isError ? (
        <PanelError error={q.error} />
      ) : q.isPending ? (
        <p className="bod-vacio">Cargando…</p>
      ) : !q.data?.length ? (
        <div className="bod-vacio bod-vacio-grande">
          <IcoCamion />
          <strong>No hay camiones por recibir</strong>
          <span>
            Cuando un chofer rinde su ODD con productos de vuelta, aparece aquí sola (se actualiza
            cada 15 segundos).
          </span>
        </div>
      ) : (
        <ul className="bod-filas">
          {q.data.map((x) => {
            const unidades = x.lineas.reduce((t, l) => t + Number(l.cantidad), 0);
            return (
              <li key={x.odId}>
                <button type="button" className="bod-camion" onClick={() => setAbierta(x.odId)}>
                  <span className="bod-camion-izq">
                    <span className="bod-camion-titulo">
                      ODD N° {x.numero}
                      <Patente valor={x.patente} />
                    </span>
                    <span className="bod-dato">
                      <b>Chofer</b> {x.chofer ?? 'sin chofer'}
                    </span>
                    <span className="bod-dato">
                      <b>Ruta</b> {x.ruta ?? '—'} · {x.fecha}
                    </span>
                  </span>
                  <span className="bod-camion-der">
                    <span className="bod-cifra">{x.lineas.length}</span>
                    <span className="bod-cifra-et">productos</span>
                    <span className="bod-cifra-et">{decimal(texto(unidades))} unid.</span>
                  </span>
                  <span className="bod-chevron" aria-hidden>
                    ›
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Recuadro>
  );
}

function Patente({ valor }: { valor: string | null }) {
  return <span className="bod-patente">{valor ?? 'S/P'}</span>;
}

function RecibirOd({
  od,
  alVolver,
  alTerminar,
}: {
  od: OdPorRecibir;
  alVolver: () => void;
  alTerminar: (r: ResultadoRecepcion) => void;
}) {
  const { empresa } = useSesion();
  const qc = useQueryClient();
  const [contado, setContado] = useState<Record<string, string>>({});
  const [obs, setObs] = useState('');
  const [confirmar, setConfirmar] = useState(false);
  const [clave] = useState(() => nuevaClaveIdempotencia('recibir'));
  const recibir = useMutation({
    mutationFn: () =>
      api.post<ResultadoRecepcion>(
        `/logistica/ordenes/${od.odId}/recibir`,
        {
          empresaId: empresa!.id,
          version: od.version,
          lineas: od.lineas.map((l) => ({
            productoId: l.productoId,
            recibido: limpiar(contado[l.productoId] ?? '0') || '0',
          })),
          observacion: obs.trim() || null,
        },
        { idempotencia: clave },
      ),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['emp', empresa!.id] });
      alTerminar(r);
    },
  });

  const filas = od.lineas.map((l) => {
    const v = contado[l.productoId] ?? '';
    const dif = v.trim() === '' ? null : num(v) - Number(l.cantidad);
    return { l, v, dif };
  });
  const contados = filas.filter((f) => f.dif !== null).length;
  const faltantes = filas.filter((f) => f.dif !== null && f.dif < 0);
  const sobrantes = filas.filter((f) => f.dif !== null && f.dif > 0);
  const totalInformado = od.lineas.reduce((t, l) => t + Number(l.cantidad), 0);
  const totalContado = filas.reduce((t, f) => t + (f.dif === null ? 0 : num(f.v)), 0);
  const completo = contados === od.lineas.length;
  const llegoTodo = () =>
    setContado(
      Object.fromEntries(
        od.lineas.map((l) => [l.productoId, decimal(l.cantidad).replace(/\./g, '')]),
      ),
    );

  return (
    <div className="bod-paso">
      <button type="button" className="bod-volver" onClick={alVolver}>
        ‹ Camiones
      </button>
      <Recuadro titulo={`ODD N° ${od.numero}`} extra={<Patente valor={od.patente} />}>
        <dl className="bod-ficha">
          <dt>Chofer</dt>
          <dd>{od.chofer ?? 'sin chofer'}</dd>
          <dt>Ruta</dt>
          <dd>{od.ruta ?? '—'}</dd>
          <dt>Fecha</dt>
          <dd>{od.fecha}</dd>
          <dt>Vuelve</dt>
          <dd>
            {od.lineas.length} producto(s) · {decimal(texto(totalInformado))} unid.
          </dd>
        </dl>
      </Recuadro>

      <Recuadro
        titulo="Conteo de lo que bajó"
        extra={
          <span data-completo={completo || undefined} className="bod-progreso">
            {contados}/{od.lineas.length} contados
          </span>
        }
      >
        <div className="bod-ayuda">
          <span>
            Cuenta y anota lo que llegó de cada producto (0 si no llegó). Lo que falte queda{' '}
            <b>a cargo del chofer</b>.
          </span>
          <button type="button" className="bod-sec" onClick={llegoTodo}>
            ✓ Llegó todo lo informado
          </button>
        </div>
        <ul className="bod-conteo">
          {filas.map(({ l, v, dif }) => (
            <li
              key={l.productoId}
              data-estado={
                dif === null ? 'pendiente' : dif < 0 ? 'falta' : dif > 0 ? 'sobra' : 'ok'
              }
            >
              <div className="bod-conteo-prod">
                <span className="bod-sku">{l.sku}</span>
                <span className="bod-conteo-nombre">{l.descripcion}</span>
              </div>
              <div className="bod-conteo-fila">
                <div className="bod-conteo-inf">
                  <span>Informado</span>
                  <b>{decimal(l.cantidad)}</b>
                </div>
                <Cantidad
                  etiqueta={`Llegó ${l.sku}`}
                  valor={v}
                  onChange={(x) => setContado((c) => ({ ...c, [l.productoId]: x }))}
                />
                <button
                  type="button"
                  className="bod-igual"
                  title="Llegó lo informado"
                  aria-label={`Llegó lo informado de ${l.sku}`}
                  onClick={() =>
                    setContado((c) => ({
                      ...c,
                      [l.productoId]: decimal(l.cantidad).replace(/\./g, ''),
                    }))
                  }
                >
                  =
                </button>
              </div>
              <div className="bod-conteo-estado">
                {dif === null
                  ? 'Por contar'
                  : dif === 0
                    ? '✓ Cuadra'
                    : dif < 0
                      ? `Falta ${decimal(texto(-dif))} · a cargo del chofer`
                      : `Sobra ${decimal(texto(dif))}`}
              </div>
            </li>
          ))}
        </ul>
        <label className="bod-campo">
          <span>Observación (opcional)</span>
          <textarea value={obs} onChange={(e) => setObs(e.target.value)} maxLength={500} rows={2} />
        </label>
      </Recuadro>

      <div className="bod-pie">
        <div className="bod-pie-totales">
          <span>
            Informado <b>{decimal(texto(totalInformado))}</b>
          </span>
          <span>
            Contado <b>{decimal(texto(totalContado))}</b>
          </span>
          <span data-malo={faltantes.length > 0 || undefined}>
            Diferencias <b>{faltantes.length + sobrantes.length}</b>
          </span>
        </div>
        {recibir.isError && <PanelError error={recibir.error} alCerrar={() => recibir.reset()} />}
        <button
          type="button"
          className="bod-primario"
          disabled={!completo || recibir.isPending}
          onClick={() =>
            faltantes.length || sobrantes.length ? setConfirmar(true) : recibir.mutate()
          }
        >
          {recibir.isPending
            ? 'Recibiendo…'
            : !completo
              ? `Falta contar ${od.lineas.length - contados} producto(s)`
              : faltantes.length || sobrantes.length
                ? `Recibir con ${faltantes.length + sobrantes.length} diferencia(s)`
                : 'Recibir: llegó todo'}
        </button>
      </div>

      {confirmar && (
        <div className="bod-velo" onClick={() => setConfirmar(false)}>
          <div
            className="bod-hoja"
            role="dialog"
            aria-label="Confirmar recepción"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Confirmar recepción · ODD N° {od.numero}</h2>
            {faltantes.length > 0 && (
              <>
                <p className="bod-hoja-sub" data-tono="malo">
                  Faltante a cargo de <b>{od.chofer ?? 'el chofer'}</b>
                </p>
                <table className="bod-tabla">
                  <tbody>
                    {faltantes.map(({ l, dif }) => (
                      <tr key={l.productoId}>
                        <td>
                          <span className="bod-sku">{l.sku}</span> {l.descripcion}
                        </td>
                        <td className="num malo">−{decimal(texto(-dif!))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {sobrantes.length > 0 && (
              <>
                <p className="bod-hoja-sub" data-tono="aviso">
                  Sobrante (entra a bodega)
                </p>
                <table className="bod-tabla">
                  <tbody>
                    {sobrantes.map(({ l, dif }) => (
                      <tr key={l.productoId}>
                        <td>
                          <span className="bod-sku">{l.sku}</span> {l.descripcion}
                        </td>
                        <td className="num">+{decimal(texto(dif!))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            <div className="bod-hoja-botones">
              <button type="button" className="bod-sec" onClick={() => setConfirmar(false)}>
                Revisar conteo
              </button>
              <button
                type="button"
                className="bod-primario"
                disabled={recibir.isPending}
                onClick={() => {
                  setConfirmar(false);
                  recibir.mutate();
                }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecepcionHecha({
  od,
  r,
  alSeguir,
}: {
  od: OdPorRecibir;
  r: ResultadoRecepcion;
  alSeguir: () => void;
}) {
  return (
    <Recuadro titulo={`ODD N° ${od.numero} recibida`} tono="ok">
      <div className="bod-hecho">
        <span className="bod-hecho-ico" aria-hidden>
          ✓
        </span>
        <dl className="bod-ficha">
          <dt>Camión</dt>
          <dd>
            {od.patente ?? 'S/P'} · {od.chofer ?? 'sin chofer'}
          </dd>
          <dt>Faltante</dt>
          <dd>
            {r.faltantes
              ? `${r.faltantes} producto(s), a cargo del chofer · AJU N° ${r.ajusteFaltanteNumero}`
              : 'Ninguno'}
          </dd>
          <dt>Sobrante</dt>
          <dd>
            {r.sobrantes
              ? `${r.sobrantes} producto(s) · AJU N° ${r.ajusteSobranteNumero}`
              : 'Ninguno'}
          </dd>
        </dl>
      </div>
      <button type="button" className="bod-primario" onClick={alSeguir}>
        Volver a camiones
      </button>
    </Recuadro>
  );
}

// ================================================================ armar ODD

interface OddCreada {
  numero: number;
  notas: number;
  camion: string | null;
  chofer: string | null;
  recaudador: string | null;
}

const kilos = (n: number) => `${decimal(texto(Math.round(n * 10) / 10))} kg`;

/**
 * Lo mismo que «Nueva ODD» del PC (POST /logistica/ordenes), pensado para el dedo: dos pasos, las notas y
 * después camión y personal de ruta. La ODD parte de la bodega elegida arriba.
 */
function ArmarOdd({ bodega }: { bodega: Ubicacion }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const q = useDisponibles(empresaId, { ubicacionId: bodega.id }, CADA);
  const vehiculos = useVehiculos(empresaId);
  const choferes = useChoferes(empresaId);
  const rutas = useRutas(empresaId);
  const [paso, setPaso] = useState<'notas' | 'datos'>('notas');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [buscar, setBuscar] = useState('');
  // null = todas las zonas; '' = notas sin zona.
  const [zona, setZona] = useState<string | null>(null);
  const [vehiculoId, setVehiculo] = useState('');
  const [choferId, setChofer] = useState('');
  const [recaudadorId, setRecaudador] = useState('');
  const [rutaId, setRuta] = useState('');
  const [fechaOd, setFechaOd] = useState(hoyLocal);
  const [nombre, setNombre] = useState('');
  const [hecha, setHecha] = useState<OddCreada | null>(null);

  // Las notas que ya están en otra ODD no se ofrecen (igual que en el PC). Si otro la toma mientras se
  // arma esta, desaparece sola de la lista y de lo elegido.
  const libres = useMemo(
    () => (q.data ?? []).filter((n) => n.enOd === null && !n.esperaNc),
    [q.data],
  );
  const zonas = useMemo(
    () => [...new Set(libres.map((n) => n.zonaNombre ?? ''))].sort((a, b) => a.localeCompare(b)),
    [libres],
  );
  const filas = libres.filter(
    (n) =>
      (zona === null || (n.zonaNombre ?? '') === zona) &&
      coincide(buscar, n.numeroVisible, n.clienteRazonSocial, n.direccionEntrega, n.zonaNombre),
  );
  const elegidas = libres.filter((n) => sel.has(n.notaId));
  const unidades = elegidas.reduce((t, n) => t + Number(n.unidadesPendientes), 0);
  const kg = elegidas.reduce((t, n) => t + Number(n.pesoKg ?? 0), 0);
  const sinPeso = elegidas.reduce((t, n) => t + (n.productosSinPeso ?? 0), 0);
  const personal = (choferes.data ?? []).filter((c) => c.activo);
  const camiones = (vehiculos.data ?? []).filter((v) => v.activo);
  const camion = camiones.find((v) => v.id === vehiculoId) ?? null;
  const capacidad = camion?.capacidadKg ? Number(camion.capacidadKg) : null;
  const ocupacion = capacidad ? Math.round((kg / capacidad) * 100) : null;
  const nombreDe = (id: string) => personal.find((c) => c.id === id)?.nombre ?? null;
  const todasVisibles = filas.length > 0 && filas.every((n) => sel.has(n.notaId));
  const alternar = (id: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const alternarVisibles = () =>
    setSel((s) => {
      const n = new Set(s);
      for (const f of filas) {
        if (todasVisibles) n.delete(f.notaId);
        else n.add(f.notaId);
      }
      return n;
    });

  const crear = useMutation({
    mutationFn: () => {
      const cuerpo = {
        empresaId,
        bodegaId: bodega.id,
        fechaNegocio: fechaOd,
        notaIds: elegidas.map((n) => n.notaId),
        ...(vehiculoId ? { vehiculoId } : {}),
        ...(choferId ? { choferId } : {}),
        ...(recaudadorId ? { recaudadorId } : {}),
        ...(nombre.trim() ? { nombre: nombre.trim() } : {}),
        ...(rutaId ? { rutaId } : {}),
      };
      return api.post<{ odId: string; numero: number }>('/logistica/ordenes', cuerpo, {
        idempotencia: intentos.clave('crear-od', cuerpo),
      });
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      setHecha({
        numero: r.numero,
        notas: elegidas.length,
        camion: camion?.patente ?? null,
        chofer: nombreDe(choferId),
        recaudador: nombreDe(recaudadorId),
      });
      setSel(new Set());
      setPaso('notas');
      setVehiculo('');
      setChofer('');
      setRecaudador('');
      setRuta('');
      setNombre('');
      await qc.invalidateQueries({ queryKey: clavesLog.logistica(empresaId) });
    },
    onError: (e) => intentos.cerrar(e),
  });

  const totales = (
    <div className="bod-pie-totales">
      <span>
        Notas <b>{elegidas.length}</b>
      </span>
      <span>
        Unidades <b>{decimal(texto(unidades))}</b>
      </span>
      <span title={sinPeso ? `${sinPeso} producto(s) sin peso en el catálogo` : undefined}>
        Kilos{' '}
        <b>
          {decimal(texto(Math.round(kg)))}
          {sinPeso ? ' *' : ''}
        </b>
      </span>
    </div>
  );

  if (hecha)
    return (
      <Recuadro titulo={`ODD N° ${hecha.numero} creada`} tono="ok">
        <div className="bod-hecho">
          <span className="bod-hecho-ico" aria-hidden>
            ✓
          </span>
          <dl className="bod-ficha">
            <dt>Notas</dt>
            <dd>{hecha.notas}</dd>
            <dt>Camión</dt>
            <dd>{hecha.camion ?? 'sin asignar'}</dd>
            <dt>Chofer</dt>
            <dd>{hecha.chofer ?? 'sin asignar'}</dd>
            <dt>Peoneta</dt>
            <dd>{hecha.recaudador ?? 'sin asignar'}</dd>
          </dl>
        </div>
        <p className="bod-vacio">
          Preparar, validar la carga y facturar se hace en el PC: Operaciones › Órdenes de despacho.
        </p>
        <button type="button" className="bod-primario" onClick={() => setHecha(null)}>
          Armar otra ODD
        </button>
      </Recuadro>
    );

  if (paso === 'datos')
    return (
      <div className="bod-paso">
        <button type="button" className="bod-volver" onClick={() => setPaso('notas')}>
          ‹ Notas
        </button>
        <Recuadro titulo="Datos de la ODD" extra={`Sale de ${bodega.codigo}`}>
          <label className="bod-campo">
            <span>Camión</span>
            <select value={vehiculoId} onChange={(e) => setVehiculo(e.target.value)}>
              <option value="">Sin asignar</option>
              {camiones.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.patente} · {v.descripcion}
                </option>
              ))}
            </select>
          </label>
          {ocupacion !== null && (
            <p className="bod-ocupacion" data-malo={ocupacion > 100 || undefined}>
              Carga {kilos(kg)} de {kilos(capacidad!)} · <b>{ocupacion} %</b>
              {ocupacion > 100 ? ' · supera la capacidad del camión' : ''}
            </p>
          )}
          <label className="bod-campo">
            <span>Chofer · vehículo y carga</span>
            <select value={choferId} onChange={(e) => setChofer(e.target.value)}>
              <option value="">Sin asignar</option>
              {personal
                .filter((c) => (c.rol ?? 'CHOFER') !== 'PEONETA_RECAUDADOR')
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
            </select>
          </label>
          <label className="bod-campo">
            <span>Peoneta recaudador · ruta y cobranza</span>
            <select value={recaudadorId} onChange={(e) => setRecaudador(e.target.value)}>
              <option value="">Sin asignar</option>
              {personal
                .filter((c) => c.rol === 'PEONETA_RECAUDADOR' || c.rol === 'AMBOS')
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
            </select>
          </label>
          <label className="bod-campo">
            <span>Ruta</span>
            <select value={rutaId} onChange={(e) => setRuta(e.target.value)}>
              <option value="">Sin ruta</option>
              {(rutas.data ?? [])
                .filter((r) => r.activo)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.codigo} · {r.nombre}
                  </option>
                ))}
            </select>
          </label>
          <label className="bod-campo">
            <span>Fecha de la ODD</span>
            <input type="date" value={fechaOd} onChange={(e) => setFechaOd(e.target.value)} />
          </label>
          <label className="bod-campo">
            <span>Nombre (opcional)</span>
            <input
              value={nombre}
              maxLength={80}
              placeholder="Ej.: Costa martes"
              onChange={(e) => setNombre(e.target.value)}
            />
          </label>
          <p className="bod-vacio">
            Camiones, choferes, peonetas y rutas se crean en el ERP: Configuración › Reparto.
          </p>
        </Recuadro>
        <div className="bod-pie">
          {totales}
          {crear.isError && <PanelError error={crear.error} alCerrar={() => crear.reset()} />}
          <button
            type="button"
            className="bod-primario"
            disabled={!elegidas.length || !fechaOd || crear.isPending}
            onClick={() => crear.mutate()}
          >
            {crear.isPending
              ? 'Creando…'
              : elegidas.length
                ? `Crear ODD con ${elegidas.length} nota(s)`
                : 'Las notas elegidas ya no están disponibles'}
          </button>
        </div>
      </div>
    );

  return (
    <>
      <Recuadro
        titulo="Notas por despachar"
        extra={q.data ? `${libres.length} pendiente${libres.length === 1 ? '' : 's'}` : undefined}
      >
        <div className="bod-buscar">
          <IcoLupa />
          <input
            type="text"
            enterKeyHint="search"
            placeholder="Cliente, NV o dirección"
            aria-label="Buscar nota"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
          />
          {buscar && (
            <button type="button" aria-label="Borrar búsqueda" onClick={() => setBuscar('')}>
              ×
            </button>
          )}
        </div>
        {zonas.length > 1 && (
          <div className="bod-chips" role="group" aria-label="Zona">
            <button type="button" aria-pressed={zona === null} onClick={() => setZona(null)}>
              Todas
            </button>
            {zonas.map((z) => (
              <button
                key={z || '-'}
                type="button"
                aria-pressed={zona === z}
                onClick={() => setZona(zona === z ? null : z)}
              >
                {z || 'Sin zona'}
              </button>
            ))}
          </div>
        )}
        {q.isError ? (
          <PanelError error={q.error} />
        ) : q.isPending ? (
          <p className="bod-vacio">Cargando…</p>
        ) : !libres.length ? (
          <div className="bod-vacio bod-vacio-grande">
            <IcoOdd />
            <strong>No hay notas por despachar en {bodega.codigo}</strong>
            <span>Las notas confirmadas con despacho pendiente aparecen aquí solas.</span>
          </div>
        ) : !filas.length ? (
          <p className="bod-vacio">Ninguna nota coincide con la búsqueda.</p>
        ) : (
          <>
            <div className="bod-ayuda">
              <span>Toca las notas que van en el camión.</span>
              <button type="button" className="bod-sec" onClick={alternarVisibles}>
                {todasVisibles ? 'Quitar estas' : `Elegir estas (${filas.length})`}
              </button>
            </div>
            <ul className="bod-notas">
              {filas.map((n) => (
                <FilaNota
                  key={n.notaId}
                  nota={n}
                  elegida={sel.has(n.notaId)}
                  alTocar={() => alternar(n.notaId)}
                />
              ))}
            </ul>
          </>
        )}
      </Recuadro>
      <div className="bod-pie">
        {totales}
        <button
          type="button"
          className="bod-primario"
          disabled={!elegidas.length}
          onClick={() => setPaso('datos')}
        >
          {elegidas.length
            ? `Seguir: camión y chofer (${elegidas.length})`
            : 'Elige al menos una nota'}
        </button>
      </div>
    </>
  );
}

function FilaNota({
  nota: n,
  elegida,
  alTocar,
}: {
  nota: NotaDisponible;
  elegida: boolean;
  alTocar: () => void;
}) {
  const entrega = n.fechaEntrega ?? n.fechaNegocio;
  return (
    <li>
      <button type="button" className="bod-nota" aria-pressed={elegida} onClick={alTocar}>
        <span className="bod-nota-marca" aria-hidden>
          {elegida ? '✓' : ''}
        </span>
        <span className="bod-camion-izq">
          <span className="bod-nota-cliente">{n.clienteRazonSocial}</span>
          <span className="bod-dato">
            <span className="bod-sku">{n.numeroVisible}</span> · {n.zonaNombre ?? 'sin zona'}
          </span>
          <span className="bod-dato">{n.direccionEntrega ?? 'sin dirección'}</span>
          <span className="bod-dato" data-otro-dia={entrega !== n.fechaNegocio || undefined}>
            Entrega {fecha(entrega)}
          </span>
        </span>
        <span className="bod-camion-der">
          <span className="bod-cifra">{decimal(n.unidadesPendientes)}</span>
          <span className="bod-cifra-et">unid.</span>
          {n.pesoKg ? <span className="bod-cifra-et">{kilos(Number(n.pesoKg))}</span> : null}
        </span>
      </button>
    </li>
  );
}

// ================================================================ ajustar stock

const MOTIVOS = {
  // Corrección primero: es el ajuste común (el conteo no cuadra) y no es una pérdida.
  SALIDA: [
    { codigo: 'CORRECCION', nombre: 'Corrección', ayuda: 'el stock estaba mal contado' },
    { codigo: 'MERMA', nombre: 'Merma', ayuda: 'se deterioró o se perdió en bodega' },
    { codigo: 'ROTURA', nombre: 'Rotura', ayuda: 'producto roto, se bota' },
    { codigo: 'VENCIMIENTO', nombre: 'Vencido', ayuda: 'producto vencido, se bota' },
    { codigo: 'ROBO_PERDIDA', nombre: 'Pérdida', ayuda: 'robo o extravío' },
  ],
  ENTRADA: [
    { codigo: 'CORRECCION', nombre: 'Corrección', ayuda: 'el stock estaba mal contado' },
    { codigo: 'SOBRANTE', nombre: 'Sobrante', ayuda: 'apareció mercadería de más' },
  ],
} as const;
type Sentido = keyof typeof MOTIVOS;

function Ajustar({ bodega, ubicaciones }: { bodega: Ubicacion; ubicaciones: Ubicacion[] }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const [producto, setProducto] = useState<ProductoEncontrado | null>(null);
  const [hecho, setHecho] = useState<{ texto: string; producto: ProductoEncontrado } | null>(null);
  const stockBodega = useStockDeBodega(empresaId, bodega.id);
  const enBodega = useMemo(
    () => new Map((stockBodega.data ?? []).map((f) => [f.productoId, f])),
    [stockBodega.data],
  );

  if (producto)
    return (
      <AjustarProducto
        producto={producto}
        bodega={bodega}
        ubicaciones={ubicaciones}
        alVolver={() => setProducto(null)}
        alGrabar={(t) => {
          setHecho({ texto: t, producto });
          setProducto(null);
        }}
      />
    );
  return (
    <>
      {hecho && (
        <Recuadro titulo="Ajuste grabado" tono="ok">
          <p className="bod-hecho-linea">{hecho.texto}</p>
          <div className="bod-fila-botones">
            <button type="button" className="bod-sec" onClick={() => setProducto(hecho.producto)}>
              Otro ajuste a este producto
            </button>
            <button type="button" className="bod-sec" onClick={() => setHecho(null)}>
              Cerrar
            </button>
          </div>
        </Recuadro>
      )}
      <BuscarProducto
        empresaId={empresaId}
        bodega={bodega}
        stock={(id) => enBodega.get(id)?.fisico ?? null}
        alElegir={(p) => {
          setProducto(p);
          setHecho(null);
        }}
      />
    </>
  );
}

function BuscarProducto({
  empresaId,
  bodega,
  stock,
  alElegir,
}: {
  empresaId: string;
  bodega: Ubicacion;
  stock: (productoId: string) => string | null;
  alElegir: (p: ProductoEncontrado) => void;
}) {
  const [t, setT] = useState('');
  const busqueda = useBuscarProductos(empresaId, useDebounce(t));
  const ref = useRef<HTMLInputElement>(null);
  return (
    <Recuadro titulo="Buscar producto" extra={`Stock en ${bodega.codigo}`}>
      <div className="bod-buscar">
        <IcoLupa />
        <input
          ref={ref}
          type="text"
          enterKeyHint="search"
          placeholder="Nombre o código"
          aria-label="Buscar producto"
          value={t}
          onChange={(e) => setT(e.target.value)}
          autoFocus
        />
        {t && (
          <button
            type="button"
            aria-label="Borrar búsqueda"
            onClick={() => {
              setT('');
              ref.current?.focus();
            }}
          >
            ×
          </button>
        )}
      </div>
      {t.trim().length < 2 ? (
        <p className="bod-vacio">
          Escribe 2 letras o más del nombre (en cualquier orden) o el código.
        </p>
      ) : busqueda.filas.length === 0 ? (
        <p className="bod-vacio">Sin resultados para «{t.trim()}».</p>
      ) : (
        <table className="bod-tabla bod-resultados">
          <thead>
            <tr>
              <th>Producto</th>
              <th className="num">Stock</th>
            </tr>
          </thead>
          <tbody>
            {busqueda.filas.slice(0, 20).map((p) => {
              const st = stock(p.id);
              return (
                <tr key={p.id} onClick={() => alElegir(p)}>
                  <td>
                    <button type="button" className="bod-enlace-fila" onClick={() => alElegir(p)}>
                      <span className="bod-sku">{p.sku}</span>
                      <span>{p.nombre}</span>
                    </button>
                  </td>
                  <td className="num" data-cero={!st || Number(st) === 0 || undefined}>
                    {st ? decimal(st) : '0'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Recuadro>
  );
}

function AjustarProducto({
  producto,
  bodega,
  ubicaciones,
  alVolver,
  alGrabar,
}: {
  producto: ProductoEncontrado;
  bodega: Ubicacion;
  ubicaciones: Ubicacion[];
  alVolver: () => void;
  alGrabar: (texto: string) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const stock = useDisponibilidad(empresaId, { productoId: producto.id });
  const fila = stock.data?.find((f) => f.ubicacionId === bodega.id);
  const otras = (stock.data ?? []).filter(
    (f) => f.ubicacionId !== bodega.id && Number(f.fisico) !== 0,
  );
  const nombreUbic = (id: string) => ubicaciones.find((u) => u.id === id)?.codigo ?? '…';
  const [sentido, setSentido] = useState<Sentido>('SALIDA');
  // Sin motivo marcado de antemano: el bodeguero lo elige siempre (una merma por defecto
  // se grababa sin querer).
  const [motivo, setMotivo] = useState<string | null>(null);
  // Como en Random: se cuenta en cajas y unidades sueltas; se graba el total en unidad base.
  const cajas = cajasDe(usePresentaciones(empresaId, producto.id).data);
  const [cajaId, setCajaId] = useState<string | null>(null);
  const caja = cajas.find((c) => c.id === cajaId) ?? cajas[0] ?? null;
  const factor = caja ? Number(caja.factor_a_base) : 1;
  const [nCajas, setNCajas] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [comentario, setComentario] = useState('');
  const [clave] = useState(() => nuevaClaveIdempotencia('ajuste-bodega'));
  const m = MOTIVOS[sentido].find((x) => x.codigo === motivo) ?? null;
  const n = (caja ? num(nCajas) * factor : 0) + num(cantidad);
  const unidad = producto.unidad_base_codigo;
  const detalle = caja && num(nCajas) ? ` (${enCajas(n, caja, unidad)})` : '';
  const antes = Number(fila?.fisico ?? 0);
  const despues = antes + (sentido === 'SALIDA' ? -n : n);
  const signo = sentido === 'SALIDA' ? '−' : '+';
  const ajustar = useMutation({
    mutationFn: () =>
      api.post<{ numero: number }>(
        '/inventario/guias-ajuste',
        {
          empresaId,
          ubicacionId: bodega.id,
          sentido,
          concepto: m!.codigo,
          // El producto va en el motivo para que el historial y el ERP se lean sin abrir la guía.
          motivo:
            `Bodega: ${m!.nombre} · ${producto.sku} ${producto.nombre}${detalle}${comentario.trim() ? ` · ${comentario.trim()}` : ''}`.slice(
              0,
              500,
            ),
          lineas: [{ productoId: producto.id, cantidad: texto(n) }],
        },
        { idempotencia: clave },
      ),
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['emp', empresaId] });
      alGrabar(
        `AJU N° ${r.numero} · ${signo}${enCajas(n, caja, unidad)} ${producto.sku} ${producto.nombre} (${m!.nombre}, ${bodega.codigo}). Stock queda en ${enCajas(despues, caja, unidad)}.`,
      );
    },
  });
  const cambiarSentido = (s: Sentido) => {
    setSentido(s);
    setMotivo(null);
  };

  return (
    <div className="bod-paso">
      <button type="button" className="bod-volver" onClick={alVolver}>
        ‹ Buscar otro producto
      </button>
      <Recuadro
        titulo="Producto"
        extra={caja ? `${caja.unidad_codigo} = ${decimal(caja.factor_a_base)} ${unidad}` : unidad}
      >
        <div className="bod-prod">
          <span className="bod-sku">{producto.sku}</span>
          <strong>{producto.nombre}</strong>
        </div>
        <div className="bod-stock">
          <div>
            <span>Físico {bodega.codigo}</span>
            <b>{fila ? decimal(fila.fisico) : stock.isPending ? '…' : '0'}</b>
            {caja && fila && <small>{enCajas(Number(fila.fisico), caja, unidad)}</small>}
          </div>
          <div>
            <span>Reservado</span>
            <b>{fila ? decimal(fila.reservado) : '0'}</b>
          </div>
          <div>
            <span>Disponible</span>
            <b>{fila ? decimal(fila.disponible) : '0'}</b>
          </div>
        </div>
        {otras.length > 0 && (
          <p className="bod-otras">
            <b>En otras ubicaciones:</b>{' '}
            {otras.map((f) => `${nombreUbic(f.ubicacionId)} ${decimal(f.fisico)}`).join(' · ')}
          </p>
        )}
      </Recuadro>

      <Recuadro titulo="Ajuste">
        <div className="bod-sentido" role="radiogroup" aria-label="Sentido">
          <button
            type="button"
            role="radio"
            aria-checked={sentido === 'SALIDA'}
            data-sentido="SALIDA"
            onClick={() => cambiarSentido('SALIDA')}
          >
            <b>− Sale</b>
            <span>baja el stock</span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={sentido === 'ENTRADA'}
            data-sentido="ENTRADA"
            onClick={() => cambiarSentido('ENTRADA')}
          >
            <b>+ Entra</b>
            <span>sube el stock</span>
          </button>
        </div>
        <div className="bod-et">Motivo</div>
        <div className="bod-motivos" role="radiogroup" aria-label="Motivo" data-sentido={sentido}>
          {MOTIVOS[sentido].map((x) => (
            <button
              key={x.codigo}
              type="button"
              role="radio"
              aria-checked={x.codigo === m?.codigo}
              onClick={() => setMotivo(x.codigo)}
            >
              {x.nombre}
            </button>
          ))}
        </div>
        <p className="bod-ayuda-motivo" data-falta={!m || undefined}>
          {m ? `${m.nombre}: ${m.ayuda}.` : 'Elige el motivo del ajuste.'}
        </p>
        {caja ? (
          <>
            {cajas.length > 1 && (
              <div className="bod-motivos bod-cajas" role="radiogroup" aria-label="Caja">
                {cajas.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    role="radio"
                    aria-checked={c.id === caja.id}
                    onClick={() => setCajaId(c.id)}
                  >
                    {c.nombre} ({decimal(c.factor_a_base)})
                  </button>
                ))}
              </div>
            )}
            <div className="bod-doble">
              <div>
                <div className="bod-et">
                  Cajas ({caja.unidad_codigo} × {decimal(caja.factor_a_base)})
                </div>
                <Cantidad etiqueta="Cajas" valor={nCajas} onChange={setNCajas} grande />
              </div>
              <div>
                <div className="bod-et">Unidades sueltas ({unidad})</div>
                <Cantidad etiqueta="Unidades" valor={cantidad} onChange={setCantidad} grande />
              </div>
            </div>
            <div className="bod-total">
              Total{' '}
              <b>
                {decimal(texto(n))} {unidad}
              </b>
              {n > 0 && num(nCajas) > 0 && <span> = {enCajas(n, caja, unidad)}</span>}
            </div>
          </>
        ) : (
          <>
            <div className="bod-et">Cantidad ({unidad})</div>
            <Cantidad etiqueta="Cantidad" valor={cantidad} onChange={setCantidad} grande />
          </>
        )}
        <div className="bod-resumen" data-sentido={sentido}>
          <span>Stock {bodega.codigo}</span>
          <b>{decimal(texto(antes))}</b>
          <span aria-hidden>→</span>
          <b data-negativo={despues < 0 || undefined}>{decimal(texto(despues))}</b>
        </div>
        {caja && (
          <p className="bod-resumen-cajas">
            {enCajas(antes, caja, unidad)} → {enCajas(despues, caja, unidad)}
          </p>
        )}
        {despues < 0 && (
          <p className="bod-alerta">
            El stock quedaría negativo: revisa la cantidad antes de grabar.
          </p>
        )}
        <label className="bod-campo">
          <span>Comentario (opcional)</span>
          <input
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            maxLength={200}
          />
        </label>
        {ajustar.isError && <PanelError error={ajustar.error} alCerrar={() => ajustar.reset()} />}
        <button
          type="button"
          className="bod-primario"
          data-sentido={sentido}
          disabled={n <= 0 || !m || ajustar.isPending}
          onClick={() => ajustar.mutate()}
        >
          {ajustar.isPending
            ? 'Grabando…'
            : n <= 0
              ? 'Escribe la cantidad'
              : !m
                ? 'Elige el motivo'
                : `Grabar ${signo}${enCajas(n, caja, unidad)} · ${m.nombre}`}
        </button>
      </Recuadro>
    </div>
  );
}

// ================================================================ historial del día

function Historial({ bodega }: { bodega: Ubicacion | null }) {
  const { empresa, usuario, puede } = useSesion();
  const empresaId = empresa!.id;
  const [solo, setSolo] = useState(true);
  const [abierta, setAbierta] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'stock', 'guias-ajuste', 'bodega', bodega?.id ?? ''],
    queryFn: () =>
      api.get<GuiaResumen[]>('/inventario/guias-ajuste', {
        query: { empresaId, ubicacionId: bodega!.id },
      }),
    enabled: !!bodega && puede('inventario.ver'),
    refetchInterval: CADA,
  });
  const hoy = hoyLocal();
  const filas = (q.data ?? []).filter(
    (g) =>
      new Date(g.creadoEn).toLocaleDateString('sv-SE') === hoy &&
      (!solo || g.creadoPor === usuario?.id),
  );
  const entradas = filas.filter((g) => g.sentido === 'ENTRADA').length;
  if (!puede('inventario.ver')) return <SinPermiso que="ver el historial de stock" />;
  return (
    <Recuadro
      titulo={`Ajustes de hoy · ${bodega?.codigo ?? ''}`}
      extra={`${filas.length} ajuste(s)`}
    >
      <div className="bod-filtro" role="radiogroup" aria-label="Quién">
        <button type="button" role="radio" aria-checked={solo} onClick={() => setSolo(true)}>
          Míos
        </button>
        <button type="button" role="radio" aria-checked={!solo} onClick={() => setSolo(false)}>
          Toda la bodega
        </button>
      </div>
      {q.isError ? (
        <PanelError error={q.error} />
      ) : q.isPending ? (
        <p className="bod-vacio">Cargando…</p>
      ) : !filas.length ? (
        <p className="bod-vacio">Sin ajustes hoy{solo ? ' hechos por ti' : ''}.</p>
      ) : (
        <>
          <p className="bod-otras">
            {filas.length - entradas} salida(s) · {entradas} entrada(s). Toca una fila para ver el
            detalle.
          </p>
          <table className="bod-tabla bod-historial">
            <thead>
              <tr>
                <th>Hora</th>
                <th>Ajuste</th>
                <th className="num">Unid.</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((g) => (
                <FilaHistorial
                  key={g.id}
                  g={g}
                  abierta={abierta === g.id}
                  alTocar={() => setAbierta((a) => (a === g.id ? null : g.id))}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
    </Recuadro>
  );
}

function FilaHistorial({
  g,
  abierta,
  alTocar,
}: {
  g: GuiaResumen;
  abierta: boolean;
  alTocar: () => void;
}) {
  const signo = g.sentido === 'SALIDA' ? '−' : '+';
  return (
    <>
      <tr onClick={alTocar} aria-expanded={abierta} data-abierta={abierta || undefined}>
        <td className="bod-mono">{hora(g.creadoEn)}</td>
        <td>
          <b>AJU {g.numero}</b>
          <span className="bod-motivo">{g.motivo.replace(/^Bodega: /, '')}</span>
        </td>
        <td className="num" data-sentido={g.sentido}>
          {signo}
          {decimal(g.unidades)}
        </td>
      </tr>
      {abierta && (
        <tr className="bod-detalle">
          <td colSpan={3}>
            <DetalleGuia id={g.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function DetalleGuia({ id }: { id: string }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = useQuery({
    queryKey: ['emp', empresaId, 'stock', 'guias-ajuste', id],
    queryFn: () => api.get<GuiaLeida>(`/inventario/guias-ajuste/${id}`, { query: { empresaId } }),
  });
  if (q.isError) return <PanelError error={q.error} />;
  if (!q.data) return <span className="bod-vacio">Cargando…</span>;
  return (
    <table className="bod-tabla">
      <thead>
        <tr>
          <th>Producto</th>
          <th className="num">Antes</th>
          <th className="num">Después</th>
        </tr>
      </thead>
      <tbody>
        {q.data.lineasDetalle.map((l) => (
          <tr key={l.numero}>
            <td>
              <NombreProducto empresaId={empresaId} id={l.productoId} />
            </td>
            <td className="num">{decimal(l.saldoAntes)}</td>
            <td className="num">
              <b>{decimal(l.saldoDespues)}</b>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NombreProducto({ empresaId, id }: { empresaId: string; id: string }) {
  const p = useProducto(empresaId, id).data?.producto;
  return p ? (
    <>
      <span className="bod-sku">{p.sku}</span> {p.nombre}
    </>
  ) : (
    <span className="bod-vacio">…</span>
  );
}

// ================================================================ íconos (trazo, heredan el color)

const svg = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;
function IcoCamion() {
  return (
    <svg {...svg} aria-hidden>
      <path d="M2 6h11v10H2zM13 9h4l3 3.5V16h-7z" />
      <circle cx="6" cy="17.5" r="1.8" />
      <circle cx="16.5" cy="17.5" r="1.8" />
    </svg>
  );
}
function IcoOdd() {
  return (
    <svg {...svg} aria-hidden>
      <path d="M8 4h8v3H8z" />
      <path d="M8 5.5H5V21h14V5.5h-3" />
      <path d="M8.5 12l2 2 4-4M8.5 17.5h7" />
    </svg>
  );
}
function IcoAjuste() {
  return (
    <svg {...svg} aria-hidden>
      <path d="M3 8l9-5 9 5v8l-9 5-9-5z" />
      <path d="M9 12h6M12 9v6" />
    </svg>
  );
}
function IcoReloj() {
  return (
    <svg {...svg} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
function IcoLupa() {
  return (
    <svg {...svg} width={18} height={18} aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4-4" />
    </svg>
  );
}
